import { Functionality, Pointer } from '@yleisradio/areena-types';
import React from 'react';
import getRoutePath from './getRoutePath';
import { NavLinkWithHistory as NavLink } from './NavLinkWithHistory';
import { To } from 'react-router-dom';
import { logger } from '../../common/logger';
import { useErrorContext } from '../error/ErrorBoundaryContext';
import { DataAttributes, ReactRouterLocationState } from '../appTypings';

type NavLinkRenderProps = {
  isActive: boolean;
  isPending: boolean;
  isTransitioning: boolean;
};

type Props = {
  functionality?: Functionality | undefined;
  pointer?: Pointer | undefined;
  id: string;
  children?: React.ReactNode | ((props: NavLinkRenderProps) => React.ReactNode);
  isGeoBlocked?: boolean;
  className?:
    | string
    | ((props: NavLinkRenderProps) => string | undefined)
    | undefined;
  role?: string;
  onClick?: React.MouseEventHandler;
  to?: To;
  replace?: boolean;
  impressionAttributes?: DataAttributes;
  dataTestId?: string;
  state?: ReactRouterLocationState;
  onMouseEnter?: React.MouseEventHandler<HTMLAnchorElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLAnchorElement>;
  onFocus?: React.FocusEventHandler<HTMLAnchorElement>;
  onBlur?: React.FocusEventHandler<HTMLAnchorElement>;
} & React.AriaAttributes;

const ReactRouterLink = ({
  functionality,
  pointer,
  id,
  children,
  className,
  role,
  onClick,
  isGeoBlocked,
  to: toOverride,
  state,
  replace,
  impressionAttributes,
  dataTestId,
  ...props
}: Props) => {
  const isNavigable = toOverride || pointer || functionality;

  if (!isNavigable) {
    logger.debug(
      'ReactRouterLink is missing all routable parameters: "to", "pointer" and "functionality"'
    );
  }

  const { notifyErrorFromComponent } = useErrorContext();

  let to: string | To = '';

  if (isNavigable && !isGeoBlocked) {
    try {
      to = toOverride ?? getRoutePath({ pointer, functionality });
    } catch (e) {
      notifyErrorFromComponent(e instanceof Error ? e : new Error(String(e)));
    }
  }

  return (
    <NavLink
      to={to}
      className={className}
      id={id}
      state={state || { areenaApiUri: pointer?.uri }}
      onClick={onClick}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
      onFocus={props.onFocus}
      onBlur={props.onBlur}
      replace={replace}
      data-testid={dataTestId}
      role={role}
      aria-label={props['aria-label']}
      {...impressionAttributes}
    >
      {children}
    </NavLink>
  );
};

export default ReactRouterLink;
